import type { HandLandmark } from './hand-detector';

export type GestureKind = 'fist' | 'scissors' | 'open_palm' | 'unknown';

export type GestureResult = {
  gesture: GestureKind;
  confidence: number;
  fingerState: { thumb: boolean; index: boolean; middle: boolean; ring: boolean; pinky: boolean };
  rawDistances: {
    thumbTip: number;
    indexTip: number;
    middleTip: number;
    ringTip: number;
    pinkyTip: number;
  };
};

const FINGER_TIPS = [4, 8, 12, 16, 20] as const;
const FINGER_PIPS = [3, 6, 10, 14, 18] as const;
const FINGER_MCPS = [2, 5, 9, 13, 17] as const;
const FINGER_NAMES = ['thumb', 'index', 'middle', 'ring', 'pinky'] as const;
const WRIST = 0;

function dist2d(a: HandLandmark, b: HandLandmark): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
}

function isFingerExtended(landmarks: HandLandmark[], tipIdx: number, pipIdx: number, mcpIdx: number): boolean {
  const wrist = landmarks[WRIST];
  const tip = landmarks[tipIdx];
  const pip = landmarks[pipIdx];
  const mcp = landmarks[mcpIdx];
  if (!wrist || !tip || !pip || !mcp) return false;
  const tipDist = dist2d(tip, wrist);
  const pipDist = dist2d(pip, wrist);
  const mcpDist = dist2d(mcp, wrist);
  return tipDist > pipDist + 0.04 && tipDist > mcpDist * 0.95;
}

function detectFingers(landmarks: HandLandmark[]): {
  thumb: boolean;
  index: boolean;
  middle: boolean;
  ring: boolean;
  pinky: boolean;
} {
  return {
    thumb: isFingerExtended(landmarks, FINGER_TIPS[0], FINGER_PIPS[0], FINGER_MCPS[0]),
    index: isFingerExtended(landmarks, FINGER_TIPS[1], FINGER_PIPS[1], FINGER_MCPS[1]),
    middle: isFingerExtended(landmarks, FINGER_TIPS[2], FINGER_PIPS[2], FINGER_MCPS[2]),
    ring: isFingerExtended(landmarks, FINGER_TIPS[3], FINGER_PIPS[3], FINGER_MCPS[3]),
    pinky: isFingerExtended(landmarks, FINGER_TIPS[4], FINGER_PIPS[4], FINGER_MCPS[4]),
  };
}

function distancesFromWrist(landmarks: HandLandmark[]): number[] {
  const wrist = landmarks[WRIST];
  if (!wrist) return [0, 0, 0, 0, 0];
  return FINGER_TIPS.map((idx) => dist2d(landmarks[idx], wrist));
}

export class GestureDetector {
  private currentGesture: GestureKind = 'unknown';
  private stableSince = 0;
  private lastFired = 0;
  private stableGesture: GestureKind = 'unknown';
  private holdMs: number;

  constructor(holdMs: number = 150, cooldownMs: number = 600) {
    this.holdMs = holdMs;
    this.lastFired = 0;
    void cooldownMs;
  }

  detect(handLandmarks: HandLandmark[] | null, now: number): GestureResult {
    const empty: GestureResult = {
      gesture: 'unknown',
      confidence: 0,
      fingerState: { thumb: false, index: false, middle: false, ring: false, pinky: false },
      rawDistances: { thumbTip: 0, indexTip: 0, middleTip: 0, ringTip: 0, pinkyTip: 0 },
    };
    if (!handLandmarks || handLandmarks.length < 21) {
      this.currentGesture = 'unknown';
      this.stableSince = 0;
      this.stableGesture = 'unknown';
      return empty;
    }

    const fingerState = detectFingers(handLandmarks);
    const dists = distancesFromWrist(handLandmarks);
    const rawDistances = {
      thumbTip: dists[0],
      indexTip: dists[1],
      middleTip: dists[2],
      ringTip: dists[3],
      pinkyTip: dists[4],
    };

    const ext = [fingerState.index, fingerState.middle, fingerState.ring, fingerState.pinky];
    const extCount = ext.filter(Boolean).length;

    let gesture: GestureKind = 'unknown';
    let confidence = 0;

    if (extCount === 4) {
      gesture = 'open_palm';
      confidence = fingerState.thumb ? 1.0 : 0.85;
    } else if (extCount === 2 && fingerState.index && fingerState.middle) {
      gesture = 'scissors';
      confidence = 0.9;
    } else if (extCount === 0) {
      gesture = 'fist';
      confidence = fingerState.thumb ? 0.8 : 1.0;
    } else if (extCount === 1 && fingerState.index) {
      gesture = 'fist';
      confidence = 0.6;
    }

    this.currentGesture = gesture;

    if (gesture !== this.stableGesture) {
      this.stableGesture = gesture;
      this.stableSince = now;
    }

    const heldLongEnough = now - this.stableSince >= this.holdMs;
    const isStable = heldLongEnough && gesture !== 'unknown';

    if (isStable && gesture !== this.currentGesture) {
    }

    if (isStable) {
      this.currentGesture = gesture;
    }

    return {
      gesture: isStable ? gesture : 'unknown',
      confidence: isStable ? confidence : 0,
      fingerState,
      rawDistances,
    };
  }

  isOnCooldown(now: number, cooldownMs: number = 600): boolean {
    return now - this.lastFired < cooldownMs;
  }

  markFired(now: number): void {
    this.lastFired = now;
  }

  getStableGesture(): GestureKind {
    return this.stableGesture;
  }
}
